/**
 * Coverage D business rules shape — mirrors `packages/brand-config-be/src/types.ts`
 * (`FarmRiskTypeConfig.businessRules.coverageD`). Defined locally so farm-utils
 * stays decoupled from brand-config-be.
 */
export type FarmCoverageDRules = {
  min?: number;
  max?: number;
  allowEditing?: boolean;
  percentOfCoverageA?: number;
};

export type FarmCoverageDBusinessRules = {
  byResidenceUsage?: Record<string, FarmCoverageDRules>;
  byLossSettlement?: Record<
    string,
    {
      byResidenceUsage: Record<string, FarmCoverageDRules>;
    }
  >;
};

const LEGACY_PERCENT_OF_COVERAGE_A = 0.2;

/**
 * Resolves the Coverage D rule entry for a given residence usage / loss settlement pair.
 *
 * FO2 defines rules via `byLossSettlement[…].byResidenceUsage[…]`; FO3 / FO1 use
 * `byResidenceUsage[…]` directly. We try the nested lookup first and fall through
 * to the flat one, matching the pattern used by `resolveFarmCoverageCRules` and
 * `farm-post-data-rules.ts`.
 */
export function resolveFarmCoverageDRules({
  residenceUsage,
  lossSettlement,
  businessRulesD
}: {
  residenceUsage?: string | null;
  lossSettlement?: string | null;
  businessRulesD?: FarmCoverageDBusinessRules;
}): FarmCoverageDRules | undefined {
  if (!businessRulesD) {
    return undefined;
  }

  if (businessRulesD.byLossSettlement && lossSettlement && residenceUsage) {
    const rules = businessRulesD.byLossSettlement[lossSettlement]?.byResidenceUsage?.[residenceUsage];
    if (rules) {
      return rules;
    }
  }

  if (businessRulesD.byResidenceUsage && residenceUsage) {
    return businessRulesD.byResidenceUsage[residenceUsage];
  }

  return undefined;
}

/**
 * Computes the default Coverage D value for a dwelling risk.
 *
 * Context: GitHub issues #30548 / follow-up — Forreston FO1/FO2/FO3 tenant-occupied
 * rentals must default to 10% of Coverage A. Primary/Seasonal stay at 20%, Vacant
 * stays at $0. The previous fix updated brand config + display components but left
 * eight hardcoded `coverageA * 0.2` write paths in the shared form code, which
 * stomped the brand-config value the moment Coverage A or Loss Settlement changed.
 *
 * Tenant rentals are back to 10% of Coverage A as of #32082 (the brief #32031
 * fixed-$3,000 rule was reverted), so they resolve via `percentOfCoverageA` below.
 *
 * Resolution order:
 *   1. Matched rule with `percentOfCoverageA` set → `coverageA * percentOfCoverageA`.
 *   2. Matched rule with no `percentOfCoverageA` (Vacant config) → `0`.
 *   3. No matched rule → legacy fallback `coverageA * 0.2`. This preserves behavior
 *      for any carrier/form/usage without configured rules.
 */
export function getDefaultFarmCoverageD({
  residenceUsage,
  lossSettlement,
  coverageA,
  businessRulesD
}: {
  residenceUsage?: string | null;
  lossSettlement?: string | null;
  coverageA: number;
  businessRulesD?: FarmCoverageDBusinessRules;
}): number {
  const rules = resolveFarmCoverageDRules({ residenceUsage, lossSettlement, businessRulesD });

  if (rules) {
    if (rules.percentOfCoverageA != null) {
      return Math.round(coverageA * rules.percentOfCoverageA);
    }
    // Matched rule with no percentOfCoverageA (e.g. Vacant { min: 0, max: 0 }) → $0.
    return 0;
  }

  return Math.round(coverageA * LEGACY_PERCENT_OF_COVERAGE_A);
}
